import React, { ReactElement, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import useNewSkin from '../../hooks/useNewSkin';
import usePriceFormatter from './usePriceFormatter';

import { getFullParsedUrl, openLink } from '../../helpers';

import {
    aiAssistantProductRecommendationClicked,
    widgetActivityTracking,
} from '../../store/actions';
import { trackingEvents } from '../../store/activityTrackingHelpers';
import { getIsMobile } from '../../store/selectors';
import { AiAssistantProduct, AiAssistantProductVariant } from '../../store/typings';
import { getTextStyles } from '../../styles/text.styles';
import Translation from '../Translation';
import { Arrow } from '../svgIcons/SvgIcons';
import AiAssistantAddToCartButton from './AiAssistantAddToCartButton';
import styles from './ProductCard.styles';

export type AiAssistantProductCardProps = {
    variant: AiAssistantProductVariant;
    product: AiAssistantProduct;
    isAddToCartEnabled: boolean;
    messageId: string;
};

const AiAssistantProductCard = ({
    variant,
    product,
    isAddToCartEnabled,
    messageId,
}: AiAssistantProductCardProps): ReactElement => {
    const dispatch = useDispatch();
    const isMobile = useSelector(getIsMobile);
    const { isNewSkin } = useNewSkin();
    const textStyles = getTextStyles(isNewSkin);
    const { base_price, price, currency, url, image_url } = variant;
    const { title } = product;
    const parsedUrl = getFullParsedUrl(url);

    const shouldShowBasePrice = base_price && price !== base_price;

    const formatter = usePriceFormatter(currency);

    const handleViewDetails = useCallback((): void => {
        dispatch(aiAssistantProductRecommendationClicked({ productId: product.product_id }));
        dispatch(widgetActivityTracking(trackingEvents.aiAssistantProductCardViewDetailsClicked));

        openLink(parsedUrl);
    }, [dispatch, parsedUrl, product.product_id]);

    return (
        <div css={styles.container}>
            <button type="button" css={styles.clickableContainer} onClick={handleViewDetails}>
                <Translation value="recommendProductViewDetails" fallback="View details" />
            </button>

            {image_url && <img src={image_url} alt={title} css={styles.image} />}

            <div css={styles.contentContainer}>
                <div css={styles.textAndPricesContainer}>
                    <p css={[styles.getTitleText(isMobile), textStyles.text14Medium]}>{title}</p>
                    {formatter && (
                        <div css={styles.pricesContainer}>
                            {shouldShowBasePrice && (
                                <p css={[styles.basePriceText, textStyles.text14Medium]}>
                                    {formatter.format(base_price)}
                                </p>
                            )}
                            <p css={[styles.priceText, textStyles.text14Medium]}>
                                {formatter.format(price)}
                            </p>
                        </div>
                    )}
                </div>

                <div css={styles.buttonsContainer}>
                    {isAddToCartEnabled ? (
                        <AiAssistantAddToCartButton
                            product={product}
                            variant={variant}
                            messageId={messageId}
                        />
                    ) : (
                        <button
                            type="button"
                            css={styles.viewDetailsButton(isNewSkin)}
                            onClick={handleViewDetails}
                        >
                            <Translation
                                value="recommendProductViewDetails"
                                fallback="View details"
                            />
                            <Arrow />
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AiAssistantProductCard;
